/**
 * Das Guthaben in der Kopfzeile (seit 03.10.2026).
 *
 * WARUM IM KOPF: Jeder Run kostet Credits, und die Frage „reicht es noch?"
 * stellte sich auf jeder Seite, beantwortet wurde sie nur auf dem Konto.
 *
 * Die Zahl kommt aus `billing/status`, wie überall (api.ts: Guthaben nie aus
 * der Tabelle). Ein Abruf je Minute reicht; die Kontoseite reicht ihren
 * frischen Stand mit `merkeGuthaben` herein, damit Kopf und Seite nicht zwei
 * verschiedene Zahlen zeigen. Scheitert der Abruf, fehlt die Zahl still: sie
 * ist eine Beigabe, die Seiten zeigen ihre Fehler selbst.
 */

import { useEffect, useState } from 'react';
import { ladeStatus, type Status } from './api';
import { useNeuLadenBeimZurueckkehren } from './neu-laden';

export interface KopfGuthaben {
  /** null: nicht abgerechnet, also unbegrenzt. */
  credits: number | null;
  /** Konto in SIM_ADMIN_KONTEN: die Kopfzeile zeigt den Reiter „Admin“. */
  admin?: boolean;
}

const FRISCH_MS = 60_000;
let stand: { zeit: number; wert: KopfGuthaben } | null = null;
let laufend: Promise<KopfGuthaben | null> | null = null;
const hoerer = new Set<(g: KopfGuthaben) => void>();

export function kopfGuthabenAus(st: Status): KopfGuthaben {
  return { credits: st.balance.metered ? Number(st.balance.balance ?? 0) : null, ...(st.admin ? { admin: true } : {}) };
}

export function merkeGuthaben(st: Status): void {
  stand = { zeit: Date.now(), wert: kopfGuthabenAus(st) };
  for (const h of hoerer) h(stand.wert);
}

function hole(erzwingen = false): Promise<KopfGuthaben | null> {
  if (!erzwingen && stand && Date.now() - stand.zeit < FRISCH_MS) return Promise.resolve(stand.wert);
  laufend ??= ladeStatus()
    .then(st => { merkeGuthaben(st); return stand!.wert; })
    .catch(e => { console.warn('[kopf] Guthaben', e); return null; })
    .finally(() => { laufend = null; });
  return laufend;
}

export function useKopfGuthaben(): KopfGuthaben | null {
  const [wert, setWert] = useState<KopfGuthaben | null>(stand?.wert ?? null);
  useEffect(() => {
    hoerer.add(setWert);
    void hole().then(g => { if (g) setWert(g); });
    return () => { hoerer.delete(setWert); };
  }, []);
  // Runs entstehen in Claude: beim Zurückkehren in den Tab neu holen.
  useNeuLadenBeimZurueckkehren(() => void hole(true));
  return wert;
}
